/**
 * Comprehensive Student Analytics Dashboard
 * Displays progress, achievements, streaks, and course recommendations
 */

import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import {
  ProgressCard,
  AchievementBadge,
  StreakCounter,
  StatsGrid
} from './AnalyticsDashboard';
import logger from '../utils/logger';

const StudentAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await analyticsAPI.getDashboardSummary();
      
      if (response.data.success) {
        setDashboardData(response.data.summary);
        logger.log('Dashboard data loaded successfully');
      } else {
        throw new Error('Failed to load dashboard data');
      }
    } catch (err) {
      logger.error('Error loading dashboard:', err);
      setError(err.userMessage || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    header: {
      marginBottom: '30px'
    },
    title: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom: '8px'
    },
    subtitle: {
      fontSize: '0.95rem',
      color: '#64748b'
    },
    tabs: {
      display: 'flex',
      gap: '10px',
      marginBottom: '30px',
      borderBottom: '2px solid #e2e8f0',
      paddingBottom: '15px'
    },
    tab: {
      padding: '10px 20px',
      fontSize: '0.95rem',
      fontWeight: '500',
      cursor: 'pointer',
      borderBottom: '3px solid transparent',
      transition: 'all 0.3s'
    },
    activeTab: {
      color: '#27b05b',
      borderBottomColor: '#27b05b'
    },
    inactiveTab: {
      color: '#64748b'
    },
    section: {
      marginBottom: '40px'
    },
    sectionTitle: {
      fontSize: '1.3rem',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px'
    },
    achievementGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: '15px'
    },
    recommendationCard: {
      padding: '20px',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      background: 'white',
      hoverEffect: true
    },
    recommendationTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '8px'
    },
    recommendationReason: {
      fontSize: '0.85rem',
      color: '#64748b',
      marginBottom: '10px'
    },
    recommendationScore: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.9rem',
      color: '#27b05b',
      fontWeight: '600'
    },
    errorBox: {
      padding: '20px',
      background: '#fee',
      border: '2px solid #fcc',
      borderRadius: '8px',
      color: '#c33',
      marginBottom: '20px'
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading your analytics..." />;
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBox}>
          <strong>⚠️ Error:</strong> {error}
          <button 
            onClick={loadDashboardData}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              background: '#27b05b',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBox}>No data available</div>
      </div>
    );
  }

  const { progress, streak, achievements, recommendations } = dashboardData;
  const earnedAchievements = achievements?.filter(a => a.earned) || [];
  const totalAchievements = achievements?.length || 0;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>📊 Your Learning Analytics</h1>
        <p style={styles.subtitle}>
          Track your progress, achievements, and get personalized course recommendations
        </p>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <div
          style={{
            ...styles.tab,
            ...(activeTab === 'overview' ? styles.activeTab : styles.inactiveTab)
          }}
          onClick={() => setActiveTab('overview')}
        >
          🎯 Overview
        </div>
        <div
          style={{
            ...styles.tab,
            ...(activeTab === 'achievements' ? styles.activeTab : styles.inactiveTab)
          }}
          onClick={() => setActiveTab('achievements')}
        >
          🏆 Achievements ({earnedAchievements.length}/{totalAchievements})
        </div>
        <div
          style={{
            ...styles.tab,
            ...(activeTab === 'recommendations' ? styles.activeTab : styles.inactiveTab)
          }}
          onClick={() => setActiveTab('recommendations')}
        >
          💡 Recommendations
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Progress Cards */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>📈 Your Progress</h2>
            <div style={styles.grid}>
              <ProgressCard
                icon="📚"
                label="Sessions Completed"
                value={progress?.sessions_completed || 0}
                subtext="learning sessions"
                color="#3b82f6"
              />
              <ProgressCard
                icon="⏱️"
                label="Study Time"
                value={`${Math.round((progress?.total_minutes || 0) / 60)}h`}
                subtext={`${progress?.total_minutes || 0} minutes`}
                color="#8b5cf6"
              />
              <ProgressCard
                icon="⭐"
                label="Average Score"
                value={`${Math.round(progress?.average_score || 0)}%`}
                subtext="across all activities"
                color="#f59e0b"
              />
              <ProgressCard
                icon="🌟"
                label="Total XP"
                value={progress?.total_xp || 0}
                subtext="experience points"
                color="#ec4899"
              />
              <ProgressCard
                icon="🎖️"
                label="Your Level"
                value={progress?.level || 1}
                subtext={`${progress?.level * 100} XP required for next`}
                color="#27b05b"
              />
              <ProgressCard
                icon="📊"
                label="Recent Activity"
                value={progress?.recent_activity_7days || 0}
                subtext="sessions in last 7 days"
                color="#06b6d4"
              />
            </div>
          </div>

          {/* Learning Streak */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>🔥 Learning Streak</h2>
            <StreakCounter days={streak || 0} maxDays={30} />
          </div>

          {/* Recent Achievements */}
          {earnedAchievements.length > 0 && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>🏅 Recent Achievements</h2>
              <div style={styles.achievementGrid}>
                {earnedAchievements.slice(0, 4).map((achievement, idx) => (
                  <AchievementBadge
                    key={idx}
                    name={achievement.name}
                    description={achievement.description}
                    earned={achievement.earned}
                    icon={achievement.icon}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Achievements Tab */}
      {activeTab === 'achievements' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>🏆 All Achievements</h2>
          <div style={styles.achievementGrid}>
            {achievements?.map((achievement, idx) => (
              <AchievementBadge
                key={idx}
                name={achievement.name}
                description={achievement.description}
                earned={achievement.earned}
                icon={achievement.icon}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recommendations Tab */}
      {activeTab === 'recommendations' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>💡 Recommended Courses</h2>
          <div style={styles.grid}>
            {recommendations?.map((rec, idx) => (
              <div
                key={idx}
                style={{
                  ...styles.recommendationCard,
                  transition: 'all 0.3s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px rgba(39, 176, 91, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <h3 style={styles.recommendationTitle}>{rec.course_title}</h3>
                <p style={styles.recommendationReason}>{rec.reason}</p>
                <div style={styles.recommendationScore}>
                  ⭐ Match: {Math.round(rec.score)}%
                </div>
              </div>
            ))}
          </div>
          {(!recommendations || recommendations.length === 0) && (
            <p style={{ color: '#64748b', marginTop: '20px' }}>
              Complete more courses to get personalized recommendations!
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentAnalyticsDashboard;
